import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Sparkles, AlertTriangle, SlidersHorizontal } from "lucide-react";

/**
 * Who is spending Booma's AI budget (round 8, decision A2).
 *
 * The cap shipped in round 7 with no way to see it, which left Robin unable to see the
 * thing he asked for it about:
 *
 *   "We cannot afford to have people in there non stop making as many offers as they
 *    want and should have a limit to total amount or at least total amount within a
 *    week/day etc."
 *
 * So this page answers two questions and no others: who is close to their limit, and
 * what is it costing in total. Sorted by percentage of cap rather than by raw count,
 * because a Bronze merchant at 19 of 20 matters more than a Gold one at 30 of 200.
 *
 * The override exists so one account can be raised without moving everyone. Clearing a
 * field returns that window to the platform default, which is the only way back.
 */
export default function AIUsage() {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ daily: "", weekly: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ai-usage`, auth);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load AI usage");
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openOverride = (row) => {
    setEditing(row);
    // Prefilled ONLY when this account has an override. Showing the default in the box
    // would turn "no override" into an override the moment somebody pressed Save.
    setForm({
      daily: row.has_override ? String(row.daily_limit) : "",
      weekly: row.has_override ? String(row.weekly_limit) : "",
    });
  };

  const saveOverride = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/accounts/${editing.account_id}/ai-limits`,
        {
          ai_daily_limit: form.daily === "" ? null : Number(form.daily),
          ai_weekly_limit: form.weekly === "" ? null : Number(form.weekly),
        },
        auth
      );
      toast.success(`Limits updated for ${editing.business_name}`);
      setEditing(null);
      await load();
    } catch (e) {
      // The backend refuses a zero with a reason worth reading, so it is shown as-is.
      toast.error(e?.response?.data?.detail || "Could not update the limits");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const rows = data?.accounts || [];
  const totals = data?.totals || {};
  const defaults = data?.defaults || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-black tracking-tight flex items-center gap-3">
          <Sparkles className="h-8 w-8 text-primary" />
          AI Usage
        </h1>
        <p className="text-muted-foreground mt-1">
          Image generation against each account&apos;s allowance. Every model call counts,
          including iterative edits and portrait extensions.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          ["Generated today", totals.generations_today ?? 0, null],
          ["Generated this week", totals.generations_this_week ?? 0, null],
          ["Accounts at their limit", totals.accounts_at_limit ?? 0,
           (totals.accounts_at_limit ?? 0) > 0 ? "text-amber-600" : null],
          ["Default allowance",
           `${defaults.daily ?? 0}/day, ${defaults.weekly ?? 0}/week`, null],
        ].map(([label, value, colour]) => (
          <Card key={label} className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
              <p className={`text-2xl font-bold mt-1 ${colour || ""}`}>{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Usage whose account has since been deleted. Surfaced rather than dropped: it
          is still spend, and a total that quietly excluded it would be wrong. */}
      {(totals.orphaned_generations ?? 0) > 0 && (
        <p className="text-xs text-muted-foreground">
          {totals.orphaned_generations} generation
          {totals.orphaned_generations === 1 ? "" : "s"} this week belong to an account
          that no longer exists. They are counted in the total above.
        </p>
      )}

      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg">By account</CardTitle>
          <CardDescription>
            Sorted by how close each is to its limit, not by raw count: an account at
            19 of 20 needs attention before one at 30 of 200.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="p-3 font-medium">Business</th>
                  <th className="p-3 font-medium text-right">Today</th>
                  <th className="p-3 font-medium text-right">This week</th>
                  <th className="p-3 font-medium text-right">Of cap</th>
                  <th className="p-3 font-medium">Allowance</th>
                  <th className="p-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-muted-foreground">
                      No advertiser accounts yet.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.account_id} className="border-b border-border last:border-0">
                    <td className="p-3">
                      <span className="font-medium">{r.business_name || r.account_id}</span>
                      {r.at_limit && (
                        <Badge variant="outline" className="ml-2 gap-1 border-amber-500/40 text-amber-600">
                          <AlertTriangle className="h-3 w-3" />
                          At limit
                        </Badge>
                      )}
                    </td>
                    <td className="p-3 text-right tabular-nums">
                      {r.used_today} <span className="text-muted-foreground">/ {r.daily_limit}</span>
                    </td>
                    <td className="p-3 text-right tabular-nums">
                      {r.used_this_week} <span className="text-muted-foreground">/ {r.weekly_limit}</span>
                    </td>
                    <td className="p-3 text-right tabular-nums">
                      <span className={r.percent_of_cap >= 80 ? "text-amber-600 font-medium" : ""}>
                        {r.percent_of_cap}%
                      </span>
                    </td>
                    <td className="p-3">
                      {r.has_override ? (
                        <Badge variant="secondary">Custom</Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">Default</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => openOverride(r)}
                        data-testid={`ai-limit-${r.account_id}`}
                      >
                        <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
                        Limits
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>AI allowance</DialogTitle>
            <DialogDescription>
              {editing?.business_name}. Leave a field empty to use the platform default
              of {defaults.daily}/day and {defaults.weekly}/week.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[["daily", "Per day"], ["weekly", "Per week"]].map(([key, label]) => (
              <div key={key} className="space-y-1">
                <label className="text-xs text-muted-foreground">{label}</label>
                <Input
                  type="number" min="1" step="1"
                  placeholder={String(defaults[key] ?? "")}
                  value={form[key]}
                  onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                  data-testid={`ai-limit-${key}`}
                />
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">
              A limit below the default is allowed, so an account running up a bill can be
              throttled. To stop an account generating entirely, suspend it rather than
              setting zero.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveOverride} disabled={saving} data-testid="ai-limit-save">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
